import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Details,
  Field,
  Input,
  Label,
  Paragraph,
  Select,
  Tag,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon, FilesIcon, PlusIcon, TrashIcon } from '@navikt/aksel-icons';
import { ChartType, QuestionType } from '../../../api/customSurveyTemplates';
import { DraftError, DraftQuestion, isChoice, questionFieldId, scaleSteps, withType } from '../templateDraft';
import { CategorySelect } from './CategorySelect';
import classes from '../CreateCustomSurveyTemplatePage.module.css';

const TYPES = [
  QuestionType.LIKERT_SCALE,
  QuestionType.NPS_SCALE,
  QuestionType.MULTIPLE_CHOICE,
  QuestionType.MULTIPLE_SELECT,
  QuestionType.SHORT_TEXT,
  QuestionType.LONG_TEXT,
  QuestionType.YES_NO,
];

const CHARTS = [ChartType.BAR, ChartType.LINE, ChartType.DONUT];

type QuestionEditorProps = {
  question: DraftQuestion;
  number: number;
  total: number;
  open: boolean;
  errors: DraftError[];
  categories: string[];
  onAddCategory: (category: string) => void;
  onChange: (question: DraftQuestion) => void;
  onToggle: () => void;
  onMove: (delta: number) => void;
  onCopy: () => void;
  onDelete: () => void;
};

const Preview = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className={classes.preview}>
    <span className={classes.hint}>{label}</span>
    {children}
  </div>
);

const ScaleBoxes = ({ steps }: { steps: number[] }) => (
  <div aria-hidden="true" className={classes.scale} style={{ gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
    {steps.map((step) => (
      <span key={step}>{step}</span>
    ))}
  </div>
);

/** Ett spørsmål som kan åpnes og redigeres, med forhåndsvisning av svaret */
export const QuestionEditor = ({
  question,
  number,
  total,
  open,
  errors,
  categories,
  onAddCategory,
  onChange,
  onToggle,
  onMove,
  onCopy,
  onDelete,
}: QuestionEditorProps) => {
  const { t } = useTranslation();
  const id = (field: string) => questionFieldId(question.key, field);
  const errorFor = (field: string) => errors.find((error) => error.fieldId === id(field));
  const message = (error?: DraftError) => (error ? t(error.message, error.params) : undefined);
  const set = (patch: Partial<DraftQuestion>) => onChange({ ...question, ...patch });
  const optionLabel = (n: number) => t('customSurveys.create.question.optionDefault', { number: n });

  const type = question.questionType;
  const title = question.questionText.trim() || t('customSurveys.create.question.untitled');
  const textError = errorFor('text');
  const scaleError = errorFor('min') ?? errorFor('max');
  const optionError = errors.find((error) => error.fieldId.startsWith(id('option')) || error.fieldId === id('add-option'));
  const steps = scaleSteps(question);

  return (
    <li className={classes.question} data-open={open} data-invalid={errors.length > 0}>
      <div className={classes.questionHead}>
        <button
          type="button"
          className={classes.questionToggle}
          aria-expanded={open}
          aria-controls={id('panel')}
          onClick={onToggle}
        >
          <span aria-hidden="true" className={classes.number}>
            {number}
          </span>
          <span className={classes.questionSummary}>
            <span className={classes.questionTitle}>
              <span className="ds-sr-only">{t('customSurveys.create.question.number', { number })} </span>
              {title}
            </span>
            <span className={classes.tags}>
              <Tag data-size="sm" data-color="accent">
                {t(`customSurveys.create.question.typeTags.${type}`)}
              </Tag>
              {question.isRequired && (
                <Tag data-size="sm" data-color="neutral">
                  {t('customSurveys.create.question.required')}
                </Tag>
              )}
              {textError && (
                <Tag data-size="sm" data-color="danger">
                  {t('customSurveys.create.question.missingText')}
                </Tag>
              )}
            </span>
          </span>
          <ChevronDownIcon aria-hidden className={classes.chevron} />
        </button>
        <div className={classes.questionActions}>
          {number > 1 && (
            <Button
              variant="tertiary"
              data-size="sm"
              icon
              aria-label={t('customSurveys.create.question.moveUp', { number })}
              onClick={() => onMove(-1)}
            >
              <ArrowUpIcon aria-hidden />
            </Button>
          )}
          {number < total && (
            <Button
              variant="tertiary"
              data-size="sm"
              icon
              aria-label={t('customSurveys.create.question.moveDown', { number })}
              onClick={() => onMove(1)}
            >
              <ArrowDownIcon aria-hidden />
            </Button>
          )}
          <Button
            variant="tertiary"
            data-size="sm"
            icon
            aria-label={t('customSurveys.create.question.copy', { number })}
            onClick={onCopy}
          >
            <FilesIcon aria-hidden />
          </Button>
          <Button
            variant="tertiary"
            data-color="danger"
            data-size="sm"
            icon
            aria-label={t('customSurveys.create.question.delete', { number })}
            onClick={onDelete}
          >
            <TrashIcon aria-hidden />
          </Button>
        </div>
      </div>

      <div id={id('panel')} hidden={!open} className={classes.questionPanel}>
        <Field>
          <Label htmlFor={id('text')}>{t('customSurveys.create.question.text')}</Label>
          <Input
            id={id('text')}
            value={question.questionText}
            aria-invalid={!!textError}
            aria-describedby={textError ? id('text-error') : undefined}
            onChange={(event) => set({ questionText: event.target.value })}
          />
          {textError && <ValidationMessage id={id('text-error')}>{message(textError)}</ValidationMessage>}
        </Field>

        <div className={classes.typeRow}>
          <Field className={classes.typeField}>
            <Label htmlFor={id('type')}>{t('customSurveys.create.question.type')}</Label>
            <Select
              id={id('type')}
              value={type}
              onChange={(event) => onChange(withType(question, event.target.value as QuestionType, optionLabel))}
            >
              {TYPES.map((value) => (
                <Select.Option key={value} value={value}>
                  {t(`customSurveys.create.question.types.${value}`)}
                </Select.Option>
              ))}
            </Select>
          </Field>
          <Checkbox
            id={id('required')}
            label={t('customSurveys.create.question.required')}
            checked={question.isRequired}
            onChange={(event) => set({ isRequired: event.target.checked })}
          />
        </div>

        {type === QuestionType.LIKERT_SCALE && (
          <fieldset className={classes.answerBox} aria-describedby={scaleError ? id('scale-error') : undefined}>
            <legend>{t('customSurveys.create.question.scale')}</legend>
            <div className={classes.scaleRange}>
              <Field>
                <Label htmlFor={id('min')} data-size="sm" className={classes.plainLabel}>
                  {t('customSurveys.create.question.from')}
                </Label>
                <Input
                  id={id('min')}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={10}
                  className={classes.numberInput}
                  value={Number.isFinite(question.scaleMin) ? question.scaleMin : ''}
                  aria-invalid={errorFor('min') ? true : undefined}
                  onChange={(event) => set({ scaleMin: event.target.value === '' ? NaN : Number(event.target.value) })}
                />
              </Field>
              <span aria-hidden="true" className={classes.to}>
                {t('customSurveys.create.question.toWord')}
              </span>
              <Field>
                <Label htmlFor={id('max')} data-size="sm" className={classes.plainLabel}>
                  {t('customSurveys.create.question.to')}
                </Label>
                <Input
                  id={id('max')}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={10}
                  className={classes.numberInput}
                  value={Number.isFinite(question.scaleMax) ? question.scaleMax : ''}
                  aria-invalid={errorFor('max') ? true : undefined}
                  onChange={(event) => set({ scaleMax: event.target.value === '' ? NaN : Number(event.target.value) })}
                />
              </Field>
            </div>
            {scaleError && <ValidationMessage id={id('scale-error')}>{message(scaleError)}</ValidationMessage>}
            {steps.length > 0 && (
              <Preview label={t('customSurveys.create.question.preview')}>
                <ScaleBoxes steps={steps} />
                <div aria-hidden="true" className={classes.scaleEnds}>
                  <span>{question.scaleMinLabel.trim() || steps[0]}</span>
                  <span>{question.scaleMaxLabel.trim() || steps[steps.length - 1]}</span>
                </div>
              </Preview>
            )}
            <Details className={classes.details}>
              <Details.Summary>{t('customSurveys.create.question.moreScale')}</Details.Summary>
              <Details.Content>
                <div className={classes.detailsContent}>
                  <div className={classes.labelGrid}>
                    <Field>
                      <Label htmlFor={id('min-label')} data-size="sm">
                        {t('customSurveys.create.question.minLabel')}
                      </Label>
                      <Input
                        id={id('min-label')}
                        placeholder={t('customSurveys.create.question.minLabelExample')}
                        value={question.scaleMinLabel}
                        onChange={(event) => set({ scaleMinLabel: event.target.value })}
                      />
                    </Field>
                    <Field>
                      <Label htmlFor={id('max-label')} data-size="sm">
                        {t('customSurveys.create.question.maxLabel')}
                      </Label>
                      <Input
                        id={id('max-label')}
                        placeholder={t('customSurveys.create.question.maxLabelExample')}
                        value={question.scaleMaxLabel}
                        onChange={(event) => set({ scaleMaxLabel: event.target.value })}
                      />
                    </Field>
                  </div>
                  <Field className={classes.chartField}>
                    <Label htmlFor={id('chart')} data-size="sm">
                      {t('customSurveys.create.question.chart')}
                    </Label>
                    <Select
                      id={id('chart')}
                      value={question.chartType ?? ChartType.BAR}
                      onChange={(event) => set({ chartType: event.target.value as ChartType })}
                    >
                      {CHARTS.map((chart) => (
                        <Select.Option key={chart} value={chart}>
                          {t(`customSurveys.create.question.charts.${chart}`)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Field>
                </div>
              </Details.Content>
            </Details>
          </fieldset>
        )}

        {type === QuestionType.NPS_SCALE && (
          <div className={classes.answerBox}>
            <Preview label={t('customSurveys.create.question.preview')}>
              <ScaleBoxes steps={steps} />
              <div aria-hidden="true" className={classes.scaleEnds}>
                <span>{t('customSurveys.create.question.npsLow')}</span>
                <span>{t('customSurveys.create.question.npsHigh')}</span>
              </div>
            </Preview>
          </div>
        )}

        {isChoice(type) && (
          <fieldset className={classes.answerBox} aria-describedby={optionError ? id('options-error') : undefined}>
            <legend>{t('customSurveys.create.question.options')}</legend>
            {question.options.map((option, index) => (
              <div key={index} className={classes.option}>
                <Label htmlFor={id(`option-${index}`)} className="ds-sr-only">
                  {t('customSurveys.create.question.optionLabel', { number: index + 1 })}
                </Label>
                <Input
                  id={id(`option-${index}`)}
                  value={option}
                  aria-invalid={optionError && !option.trim() ? true : undefined}
                  onChange={(event) =>
                    set({ options: question.options.map((value, other) => (other === index ? event.target.value : value)) })
                  }
                />
                <Button
                  variant="tertiary"
                  data-color="danger"
                  data-size="sm"
                  icon
                  aria-label={t('customSurveys.create.question.removeOption', { number: index + 1 })}
                  onClick={() => set({ options: question.options.filter((_, other) => other !== index) })}
                >
                  <TrashIcon aria-hidden />
                </Button>
              </div>
            ))}
            {optionError && <ValidationMessage id={id('options-error')}>{message(optionError)}</ValidationMessage>}
            <Button
              id={id('add-option')}
              variant="tertiary"
              data-size="sm"
              className={classes.startButton}
              onClick={() => set({ options: [...question.options, optionLabel(question.options.length + 1)] })}
            >
              <PlusIcon aria-hidden />
              {t('customSurveys.create.question.addOption')}
            </Button>
          </fieldset>
        )}

        {(type === QuestionType.SHORT_TEXT || type === QuestionType.LONG_TEXT) && (
          <div className={classes.answerBox}>
            <Preview label={t('customSurveys.create.question.preview')}>
              <div aria-hidden="true" className={classes.textBox} data-long={type === QuestionType.LONG_TEXT} />
            </Preview>
            <Paragraph data-size="xs" className={classes.subtle}>
              {t('customSurveys.create.question.textPrivacy')}
            </Paragraph>
          </div>
        )}

        {type === QuestionType.YES_NO && (
          <div className={classes.answerBox}>
            <Preview label={t('customSurveys.create.question.preview')}>
              <div aria-hidden="true" className={classes.yesNo}>
                <span>{t('customSurveys.create.question.yes')}</span>
                <span>{t('customSurveys.create.question.no')}</span>
              </div>
            </Preview>
          </div>
        )}

        <CategorySelect
          id={id('category')}
          value={question.category}
          categories={categories}
          description={t('customSurveys.create.question.categoryDescription')}
          onChange={(category) => set({ category })}
          onAdd={onAddCategory}
        />
      </div>
    </li>
  );
};
